import { c as _c } from "react/compiler-runtime";
import { useState, useEffect } from 'react';
import { useReadContract, useWriteContract, useWalletClient } from 'wagmi';
import { type DirectListing, type DirectListingParameters } from '@chewy/typescript-config/marketplace-types';
import { getMarketplaceContract } from '@/constants/marketplace/contracts';
import { directListingsAbi } from '@/constants/marketplace/abi/directListings';
import { parseUnits, formatUnits, type Address } from 'viem';
import toast from '@/util/toast';
import { readContract } from 'wagmi/actions';
import type { ChainType } from '@/constants/chain-specific';
import { config } from '@/config/index';
type DirectListingsParams = {
  chainId?: ChainType;
  contractAddress?: Address;
  collectionAddress?: Address;
  listingId?: bigint;
};
export function useDirectListing(t0) {
  const $ = _c(17);
  const params = t0 === undefined ? 109 : t0;
  const chainId = typeof params === "object" ? params.chainId || 109 : params;
  typeof params === "object" ? params.contractAddress : undefined;
  typeof params === "object" ? params.collectionAddress : undefined;
  const listingId = typeof params === "object" ? params.listingId : undefined;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  useState(t1);
  const [loading] = useState(false);
  const [error] = useState(null);
  let t2;
  if ($[1] !== chainId) {
    t2 = getMarketplaceContract(chainId);
    $[1] = chainId;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const marketplaceContract = t2;
  const contractAddress = marketplaceContract?.address;
  const t3 = contractAddress as Address;
  const t4 = listingId as bigint;
  let t5;
  if ($[3] !== t4) {
    t5 = [t4];
    $[3] = t4;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  const t6 = !!contractAddress && !!listingId;
  let t7;
  if ($[5] !== t6) {
    t7 = {
      enabled: t6
    };
    $[5] = t6;
    $[6] = t7;
  } else {
    t7 = $[6];
  }
  let t8;
  if ($[7] !== chainId || $[8] !== t3 || $[9] !== t5 || $[10] !== t7) {
    t8 = {
      address: t3,
      abi: directListingsAbi,
      functionName: "getListing",
      args: t5,
      query: t7,
      chainId
    };
    $[7] = chainId;
    $[8] = t3;
    $[9] = t5;
    $[10] = t7;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  const {
    data: listing,
    refetch
  } = useReadContract(t8);
  let t9;
  if ($[12] !== error || $[13] !== listing || $[14] !== loading || $[15] !== refetch) {
    t9 = {
      listing,
      loading,
      error,
      refetch
    };
    $[12] = error;
    $[13] = listing;
    $[14] = loading;
    $[15] = refetch;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  return t9;
}
export function useDirectListings(params: DirectListingsParams | ChainType = 109) {
  // Handle both the new object format and backward compatibility with just chainId
  const chainId = typeof params === 'object' ? params.chainId || 109 : params;
  const providedContractAddress = typeof params === 'object' ? params.contractAddress : undefined;
  const collectionAddress = typeof params === 'object' ? params.collectionAddress : undefined;
  const [listings, setListings] = useState<DirectListing[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const marketplaceContract = getMarketplaceContract(chainId);
  const contractAddress = marketplaceContract?.address;
  const {
    data: walletClient
  } = useWalletClient();

  // Get total number of listings
  const {
    data: totalListings,
    refetch: refetchTotalListings
  } = useReadContract({
    address: contractAddress as Address,
    abi: directListingsAbi,
    functionName: 'totalListings',
    query: {
      enabled: !!contractAddress
    },
    chainId
  });

  // Fetch all valid listings
  const fetchListings = async () => {
    if (!contractAddress) return;
    try {
      setLoading(true);
      setError(null);
      const total = Number(totalListings || 0n);
      if (total > 0) {
        // Fetch listings in batches (e.g., 10 at a time)
        const batchSize = 100;
        const batches = Math.ceil(total / batchSize);
        // Array to collect all listings
        const allListings: DirectListing[] = [];
        for (let i = 0; i < batches; i++) {
          const startId = BigInt(i * batchSize);
          const endId = BigInt(Math.min((i + 1) * batchSize - 1, total - 1));
          console.log("getting listings from: ", startId, " to ", endId);
          const batchListings = await readContract(config, {
            address: contractAddress as Address,
            abi: directListingsAbi,
            functionName: 'getAllValidListings',
            args: [startId, endId],
            chainId
          });
          console.log(batchListings);
          if (batchListings) {
            if (collectionAddress) {
              // If collection address is provided, filter listings
              for (const listing of batchListings) {
                if (listing.assetContract.toLowerCase() === collectionAddress.toLowerCase()) {
                  // Only add listings that match the collection address
                  allListings.push(listing as DirectListing);
                }
              }
            } else {
              // If no collection address filter, add all listings
              allListings.push(...(batchListings as DirectListing[]));
            }
          }
        }
        setListings(allListings);
      }
    } catch (err) {
      console.error('Error fetching listings:', err);
      setError(err instanceof Error ? err : new Error('Failed to fetch listings'));
    } finally {
      setLoading(false);
    }
  };

  // Create listing
  const {
    writeContract: createListingAsync,
    data: createListingData,
    error: createListingError,
    isPending: isCreatingListing
  } = useWriteContract();

  // Update listing
  const {
    writeContract: updateListingAsync,
    data: updateListingData,
    error: updateListingError,
    isPending: isUpdatingListing
  } = useWriteContract();

  // Cancel listing
  const {
    writeContract: cancelListingAsync,
    data: cancelListingData,
    error: cancelListingError,
    isPending: isCancellingListing
  } = useWriteContract();

  // Buy from listing
  const {
    writeContract: buyFromListingAsync,
    data: buyFromListingData,
    error: buyFromListingError,
    isPending: isBuyingFromListing
  } = useWriteContract();

  // Create a new direct listing
  const createListing = async (assetContract: string, tokenId: bigint, quantity: bigint, currency: string, pricePerToken: string, startTimestamp: number, endTimestamp: number, reserved: boolean = false) => {
    if (!contractAddress || !walletClient) {
      throw new Error('Contract address or wallet not available');
    }
    try {
      // Convert price from string to bigint with appropriate decimals
      const decimalBigInt = parseUnits(pricePerToken, 18);
      const params_0: DirectListingParameters = {
        assetContract: assetContract as Address,
        tokenId,
        quantity,
        currency: currency as Address,
        pricePerToken: decimalBigInt,
        startTimestamp: BigInt(startTimestamp),
        endTimestamp: BigInt(endTimestamp),
        reserved
      };
      const tx = await createListingAsync({
        address: contractAddress as Address,
        abi: directListingsAbi,
        functionName: 'createListing',
        args: [params_0 as any]
      });
      toast.success('Listing created! Transaction sent.');
      return tx;
    } catch (err_0) {
      console.error('Error creating listing:', err_0);
      toast.error('Failed to create listing');
      throw err_0;
    }
  };

  // Update an existing listing
  const updateListing = async (listingId: bigint, assetContract_0: string, tokenId_0: bigint, quantity_0: bigint, currency_0: string, pricePerToken_0: string, startTimestamp_0: number, endTimestamp_0: number, reserved_0: boolean = false) => {
    if (!contractAddress || !walletClient) {
      throw new Error('Contract address or wallet not available');
    }
    try {
      // Convert price from string to bigint with appropriate decimals
      const decimalBigInt_0 = parseUnits(pricePerToken_0, 18);
      const params_1: DirectListingParameters = {
        assetContract: assetContract_0 as Address,
        tokenId: tokenId_0,
        quantity: quantity_0,
        currency: currency_0 as Address,
        pricePerToken: decimalBigInt_0,
        startTimestamp: BigInt(startTimestamp_0),
        endTimestamp: BigInt(endTimestamp_0),
        reserved: reserved_0
      };

      // Pass each parameter directly instead of passing the struct
      const tx_0 = await updateListingAsync({
        address: contractAddress as Address,
        abi: directListingsAbi,
        functionName: 'updateListing',
        args: [listingId, params_1 as any]
      });
      toast.success('Listing updated! Transaction sent.');
      return tx_0;
    } catch (err_1) {
      console.error('Error updating listing:', err_1);
      toast.error('Failed to update listing');
      throw err_1;
    }
  };

  // Cancel an existing listing
  const cancelListing = async (listingId_0: bigint) => {
    if (!contractAddress || !walletClient) {
      throw new Error('Contract address or wallet not available');
    }
    try {
      const tx_1 = await cancelListingAsync({
        address: contractAddress as Address,
        abi: directListingsAbi,
        functionName: 'cancelListing',
        args: [listingId_0]
      });
      toast.success('Listing cancelled! Transaction sent.');
      return tx_1;
    } catch (err_2) {
      console.error('Error cancelling listing:', err_2);
      toast.error('Failed to cancel listing');
      throw err_2;
    }
  };

  // Buy an NFT from a listing
  const buyFromListing = async (listingId_1: bigint, buyFor: string, quantity_1: bigint, currency_1: string, expectedTotalPrice: string, isNativeCurrency: boolean = false) => {
    if (!contractAddress || !walletClient) {
      throw new Error('Contract address or wallet not available');
    }
    try {
      // Convert price from string to bigint with appropriate decimals
      const decimalBigInt_1 = parseUnits(expectedTotalPrice, 18);
      const value = isNativeCurrency ? decimalBigInt_1 : 0n;
      const tx_2 = await buyFromListingAsync({
        address: contractAddress as Address,
        abi: directListingsAbi,
        functionName: 'buyFromListing',
        args: [listingId_1, buyFor as Address, quantity_1, currency_1 as Address, decimalBigInt_1],
        value
      });
      toast.success('Purchase successful! Transaction sent.');
      return tx_2;
    } catch (err_3) {
      console.error('Error buying from listing:', err_3);
      toast.error('Failed to buy from listing');
      throw err_3;
    }
  };

  // Load listings on mount and when chain or contract changes
  useEffect(() => {
    if (contractAddress) {
      fetchListings();
    }
  }, [contractAddress, totalListings, collectionAddress]);
  return {
    listings,
    loading,
    error,
    refreshListings: fetchListings,
    createListing,
    updateListing,
    cancelListing,
    buyFromListing,
    isCreatingListing,
    isUpdatingListing,
    isCancellingListing,
    isBuyingFromListing
  };
}